import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { logger } from '../utils/logger';
import { FieldVisibilityContext } from '../contexts/contexts';

/**
 * Props for the FieldPrivacyToggle component.
 */
interface FieldPrivacyToggleProps {
  /** The dot-notation path to the field (e.g., "describe.title", "measure.dimensions") */
  fieldPath: string;
  /** Whether to show a text label next to the icon. Defaults to false. */
  showLabel?: boolean;
  /** Size of the toggle button. Defaults to 'small'. */
  size?: 'small' | 'medium' | 'large';
  /** Whether the toggle is disabled. Defaults to false. */
  disabled?: boolean;
}

/**
 * Toggle button for field-level privacy control.
 * - Lock icon (grey) = private
 * - Public icon (green) = public
 *
 * While the initial visibility settings are loading, always renders the lock
 * icon (private default) to prevent a brief flash of the public globe icon
 * before the actual settings are fetched. See issue #857.
 */
export const FieldPrivacyToggle: React.FC<FieldPrivacyToggleProps> = ({
  fieldPath,
  showLabel = false,
  size = 'small',
  disabled = false,
}) => {
  // Read context directly (rather than via the hook) so we can render null when
  // no provider is mounted. This keeps the toggle a drop-in addition for row
  // patterns whose isolated component tests don't yet wrap a FieldVisibilityProvider.
  const ctx = React.useContext(FieldVisibilityContext);
  if (!ctx) return null;
  const { getFieldVisibility, updateFieldVisibility, loading } = ctx;
  const visibility = getFieldVisibility(fieldPath);

  // While loading, treat the field as private to prevent globe flash on initial render.
  // Only show the actual visibility state once loading has completed.
  const isPublic = !loading && visibility === 'public';

  const handleToggle = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion collapse/expand when clicking the toggle
    if (disabled || loading) return;
    try {
      await updateFieldVisibility(fieldPath, isPublic ? 'private' : 'public');
    } catch (error) {
      logger.error('[FieldPrivacyToggle] Failed to toggle visibility', error);
    }
  };

  const tooltipText = isPublic
    ? 'Public - Click to make private'
    : 'Private - Click to make public';

  const iconSize = size === 'small' ? 18 : size === 'medium' ? 24 : 32;

  return (
    <Box display="inline-flex" alignItems="center" gap={0.5}>
      <Tooltip title={tooltipText} arrow>
        <span>
          <IconButton
            onClick={handleToggle}
            disabled={disabled || loading}
            size={size}
            sx={{
              color: isPublic ? 'success.main' : 'text.secondary',
              '&:hover': {
                backgroundColor: isPublic ? 'success.light' : 'action.hover',
              },
            }}
          >
            {isPublic ? (
              <PublicIcon sx={{ fontSize: iconSize }} />
            ) : (
              <LockIcon sx={{ fontSize: iconSize }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {showLabel && (
        <Box
          component="span"
          sx={{
            fontSize: size === 'small' ? '0.75rem' : '0.875rem',
            color: isPublic ? 'success.main' : 'text.secondary',
            fontWeight: 500,
          }}
        >
          {isPublic ? 'Public' : 'Private'}
        </Box>
      )}
    </Box>
  );
};

export default FieldPrivacyToggle;
